<template>
  <v-container class="app-container--width-max">
    <v-card
      :elevation="elevation"
      class="ma-0 quote-summary__card"
      :class="[
        $vuetify.breakpoint.mdAndUp ? 'rounded-lg' : 'rounded-0',
        isMPPI ? '' : 'px-4 px-md-10 py-6',
      ]"
    >
      <div class="pa-0">
        <BaseButton
          :text="backButtonText"
          flat
          style="margin-left: -7px"
          depressed
          ripple
          class="font-weight-bold pa-0"
          icon="mdi-chevron-left"
          color="#106175"
          small
          @click="backToPropertyDetails()"
        />
        <div class="d-flex flex-column align-start ma-0 mt-6">
          <div class="d-flex align-self-stretch">
            <h2 class="hidden-sm-and-down font-28 font-weight-bold">
              {{ heading }}
            </h2>
            <h2 class="hidden-md-and-up font-24 font-weight-bold">
              {{ headingSm }}
            </h2>
            <BaseButton
              v-if="hasQuotes || isShowSaveAndExitButton"
              :text="$vuetify.breakpoint.lgAndUp ? 'Save &amp; exit' : 'Save'"
              flat
              depressed
              ripple
              class="font-weight-bold pa-0 ml-auto"
              icon="mdi-content-save"
              color="#106175"
              small
              @click="loadSaveAndExitDialog()"
            />
          </div>
          <div>
            <v-tooltip
              bottom
              open-on-click
              :open-on-hover="false"
              v-model="showRefCopyTooltip"
              content-class="qs-tooltip"
            >
              <template v-slot:activator="{ on }">
                <span
                  v-on="on"
                  class="referenceNumber"
                  :style="{
                    'background-color': showRefCopyTooltip ? '#FAB519' : '',
                  }"
                  @mouseover="showRefCopyIcon = true"
                  @mouseleave="
                    showRefCopyIcon = false;
                    showRefCopyTooltip = false;
                  "
                  @click="copyText(quoteReference)"
                >
                  <span class="referenceNumber__text">
                    REF:&nbsp;{{ quoteReference }}
                  </span>

                  <span
                    class="referenceNumber__icon ml-2"
                    aria-hidden="true"
                    v-show="showRefCopyIcon || $vuetify.breakpoint.mobile"
                  >
                    <v-icon small class="referenceNumber__icon--v">
                      icon-content-copy
                    </v-icon>
                  </span>
                </span>
              </template>
              <span>Copied!</span>
            </v-tooltip>
          </div>
        </div>
        <div class="d-flex mt-4" v-if="isShowChangeCover">
          <h2
            class="d-flex align-self-center font-16 font-md-20 font-weight-bold"
          >
            {{ cover.type }} cover
          </h2>
          <BaseButton
            v-if="!isTenantsContents"
            text="Change"
            flat
            link
            icon="mdi-pencil"
            icon-size="20"
            depressed
            color="var(--blue-500)"
            ripple
            small
            class="font-weight-medium font-16 px-1 d-flex align-self-center ml-2"
            @click="$emit('showDrawerChangeCover')"
          />
        </div>
      </div>
      <div
        class="d-flex bg-blue-50 flex-wrap flex-lg-nowrap mt-6 mx-n4 mx-md-0 pa-md-5"
        :class="$vuetify.breakpoint.mdAndUp ? 'rounded' : 'rounded-0'"
      >
        <template v-if="isBuildingsCover">
          <div class="d-flex flex-column pl-3 pt-4 pt-md-0 pb-3 flex-grow-1">
            <h3 class="font-16">Buildings cover</h3>
            <div>
              Amount:
              <span class="font-weight-bold">{{
                cover.buildingsCover.amount
                  | formatCurrency(AMOUNT_INSURED_FORMAT)
              }}</span>
            </div>
            <div>
              Excess:
              <span class="font-weight-bold">{{
                cover.buildingsCover.excess
                  | formatCurrency(AMOUNT_INSURED_FORMAT)
              }}</span>
            </div>
            <div>
              No claims:
              <span class="font-weight-bold">{{
                cover.buildingsCover.noClaim === 1
                  ? `${cover.buildingsCover.noClaim} year`
                  : `${cover.buildingsCover.noClaim} years`
              }}</span>
            </div>
          </div>
          <v-divider
            vertical
            class="mx-3 mt-4 mt-md-0 d-none d-sm-block"
          ></v-divider>
        </template>
        <template v-if="isContentsCover || isContentsOnlyProduct">
          <div
            class="d-flex flex-column pl-2 pr-1 pt-4 pt-md-0 flex-grow-1 content-cover__wrapper"
          >
            <h3 class="font-16">Contents cover</h3>
            <div>
              Amount:
              <span class="font-weight-bold">{{
                cover.contentsCover.amount
                  | formatCurrency(AMOUNT_INSURED_FORMAT)
              }}</span>
            </div>
            <div class="d-flex">
              Excess:&nbsp;
              <span class="font-weight-bold">
                {{
                  cover.contentsCover.excess
                    | formatCurrency(AMOUNT_INSURED_FORMAT)
                }}</span
              >
            </div>
            <div>
              No claims:
              <span class="font-weight-bold">{{
                cover.contentsCover.noClaim === 1
                  ? `${cover.contentsCover.noClaim} year`
                  : `${cover.contentsCover.noClaim} years`
              }}</span>
            </div>
          </div>
          <v-divider vertical class="mx-3 d-none d-lg-block"></v-divider>
        </template>

        <template v-if="isMPPI">
          <div class="ma-4 ma-md-0 flex-grow-1 lh-7">
            <h4>Cover options</h4>
            <!-- AS layout -->
            <v-container v-if="isAccidentAndSickness">
              <v-row no-gutters>
                <v-col cols="12" lg="6">
                  <div>
                    Cover type:
                    <span class="font-weight-bold">{{ cover.type }}</span>
                  </div>
                  <div>
                    Benefit period:
                    <span class="font-weight-bold">{{
                      cover.benefitPeriod
                    }}</span>
                  </div>
                </v-col>
                <v-col cols="12" lg="6">
                  <div>
                    Qualification type:
                    <span class="font-weight-bold">{{
                      cover.disabilityQualificationType
                    }}</span>
                  </div>
                  <div>
                    Qualification period:
                    <span class="font-weight-bold">{{
                      cover.disabilityQualificationPeriod
                    }}</span>
                  </div>
                </v-col>
              </v-row>
            </v-container>

            <!-- ASU layout -->
            <v-container v-if="isAccidentSicknessAndUnemployment">
              <v-row no-gutters>
                <v-col cols="12" lg="4">
                  <div class="d-flex flex-column fill-height">
                    <div>
                      Cover type:
                      <span class="font-weight-bold">{{ cover.type }}</span>
                    </div>
                    <div>
                      Benefit period:
                      <span class="font-weight-bold">{{
                        cover.benefitPeriod
                      }}</span>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" lg="4">
                  <div class="d-flex flex-column flex-lg-row fill-height">
                    <v-divider
                      vertical
                      class="d-none d-lg-block mx-4"
                    ></v-divider>
                    <v-divider class="my-4 my-lg-0 d-lg-none"></v-divider>
                    <div>
                      <h4>Accident & sickness</h4>
                      <div>
                        Type:
                        <span class="font-weight-bold">{{
                          cover.disabilityQualificationType
                        }}</span>
                      </div>
                      <div>
                        Period:
                        <span class="font-weight-bold">{{
                          cover.disabilityQualificationPeriod
                        }}</span>
                      </div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" lg="4">
                  <div class="d-flex flex-column flex-lg-row fill-height">
                    <v-divider
                      vertical
                      class="d-none d-lg-block mx-4"
                    ></v-divider>
                    <v-divider class="my-4 my-lg-0 d-lg-none"></v-divider>
                    <div>
                      <h4>Unemployment</h4>
                      <div>
                        Type:
                        <span class="font-weight-bold">{{
                          cover.unemploymentQualificationType
                        }}</span>
                      </div>
                      <div>
                        Period:
                        <span class="font-weight-bold">{{
                          cover.unemploymentQualificationPeriod
                        }}</span>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </div>
        </template>

        <template v-if="!isMPPI">
          <div
            class="d-flex flex-column mt-4 pl-3 pl-md-2 mt-lg-0 pt-4 pb-3 pb-md-0 pb-xl-0 pt-lg-0 additional-content flex-grow-1"
          >
            <h3 class="font-16">Additional cover</h3>
            <div
              class="d-flex align-center"
              v-if="cover.hasOwnProperty('homeEmergency')"
            >
              Home emergency:&nbsp;
              <span class="font-weight-bold">{{
                cover.homeEmergency ? "Included" : "Not included"
              }}</span>
              <v-icon color="orange darken-1" v-if="!cover.homeEmergency" small
                >mdi-alert-circle</v-icon
              >
            </div>
            <div
              class="d-flex align-center"
              v-if="Object.hasOwn(cover, 'landlordsEmergency')"
            >
              Landlord’s Emergency:&nbsp;
              <span class="font-weight-bold">{{
                cover.landlordsEmergency ? "Included" : "Not included"
              }}</span>
              <v-icon
                color="orange darken-1"
                v-if="!cover.landlordsEmergency"
                small
                >mdi-alert-circle</v-icon
              >
            </div>
            <div
              v-if="cover.hasOwnProperty('legalExpenses')"
              class="d-flex align-center"
            >
              Legal expenses:&nbsp;
              <span class="font-weight-bold">{{
                cover.legalExpenses ? "Included" : "Not included"
              }}</span>
              <v-icon color="orange darken-1" v-if="!cover.legalExpenses" small
                >mdi-alert-circle</v-icon
              >
            </div>
            <div v-if="cover.hasOwnProperty('valuables')">
              Valuables:
              <span class="font-weight-bold">{{
                formatRepeatingGroupsCount(cover.valuables)
              }}</span>
              <v-icon
                color="orange darken-1"
                small
                v-if="!cover.valuables.length"
                >mdi-alert-circle</v-icon
              >
            </div>
            <div v-if="cover.hasOwnProperty('worldwideBelongings')">
              Worldwide belongings:
              <span class="font-weight-bold">{{
                formatRepeatingGroupsCount(cover.worldwideBelongings)
              }}</span>
              <v-icon
                color="orange darken-1"
                small
                v-if="!cover.worldwideBelongings.length"
                >mdi-alert-circle</v-icon
              >
            </div>
            <div
              v-if="
                (isContentsCover || isContentsOnlyProduct) &&
                Object.hasOwn(cover, 'personalPossessions')
              "
            >
              Specified items:
              <span class="font-weight-bold">{{
                cover.personalPossessions === 1
                  ? `${cover.personalPossessions} item`
                  : `${cover.personalPossessions} items`
              }}</span>
            </div>
            <div v-if="Object.hasOwn(cover, 'rentGuarantee')">
              Rent Protection:
              <span class="font-weight-bold">{{
                cover.rentGuarantee ? "Included" : "Not included"
              }}</span>
              <v-icon color="orange darken-1" v-if="!cover.rentGuarantee" small
                >mdi-alert-circle</v-icon
              >
            </div>
          </div>
        </template>

        <div class="align-center d-none d-xl-flex">
          <BaseButton
            text="CUSTOMISE"
            rounded
            depressed
            ripple
            color="#106175"
            height="60"
            class="white--text font-weight-bold px-10 font-20"
            @click="$emit('click')"
          />
        </div>
      </div>
      <div class="d-flex justify-center d-xl-none mt-4">
        <BaseButton
          text="CUSTOMISE"
          rounded
          block
          depressed
          ripple
          height="60"
          color="#106175"
          @click="$emit('click')"
          class="white--text font-weight-bold font-20"
        />
        <BaseDialog
          ref="saveAndExitDialog"
          headerIcon="mdi-content-save"
          headerIconColor="#106175"
          windowClass="pa-0 pb-xl-0"
          headerClass="pa-5 mb-n1"
          headerDivider
          no-click-animation
          :max-width="620"
        >
          <template v-slot:content="{}">
            <SaveAndExit
              @saveAndExit="saveAndExit"
              @close="closeSaveAndExitDialog"
            />
          </template>
        </BaseDialog>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { QUESTIONS_MODULE, STEPS_MODULE } from "@/store/types/module-types";
import { formatRepeatingGroupsCount } from "@/common/answers";
import { AMOUNT_INSURED_FORMAT } from "@/constants/currency-format.constants";
import { COVERTYPE } from "@/constants/common.constants";
import BuildingsContentsVisibility from "@/Mixins/QuoteResults/BuildingsContentsVisibility";
import SaveAndExit from "@/views/Dialog/SaveAndExit";

export default {
  components: {
    SaveAndExit,
    BaseButton: () => import("@/components/BaseComponents/BaseButton.vue"),
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
  },
  mixins: [BuildingsContentsVisibility],
  props: {
    cover: {
      type: Object,
      required: true,
    },
    section: {
      type: Object,
      required: false,
    },
    sections: {
      type: Array,
      required: false,
    },
    handleBackBtnExternally: Boolean,
    headingSm: {
      type: String,
      default: "Your quotes",
    },
    heading: {
      type: String,
      default: "Your quotes",
    },
    isShowSaveAndExitButton: {
      type: Boolean,
      default: false,
    },
    backButtonText: {
      type: String,
      default: "Back to 'Property details'",
    },
    isShowChangeCover: {
      type: Boolean,
      default: true,
    },
    elevation: {
      type: [String, Number],
      default: 2,
    },
  },
  data() {
    return {
      quotes: [],
      AMOUNT_INSURED_FORMAT,
      showRefCopyIcon: false,
      showRefCopyTooltip: false,
    };
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "hasQuotes",
      "quoteReference",
      "isSelfServe",
      "replacementProductAvailable",
      "isMPPI",
      "isTenantsContents",
    ]),
    ...mapState(QUESTIONS_MODULE, ["overlay", "questionMap"]),
    isAccidentAndSickness() {
      return this.questionMap["CoverType"].Value === COVERTYPE.Disability;
    },
    isAccidentSicknessAndUnemployment() {
      return (
        this.questionMap["CoverType"].Value ===
        COVERTYPE.UnemploymentAndDisability
      );
    },
  },
  methods: {
    ...mapActions(STEPS_MODULE, ["handleBack"]),
    ...mapActions(QUESTIONS_MODULE, [
      "handleSaveAndExitOnResults",
      "handleD2CSaveAndExitOnResults",
      "setOverlayVisible",
      "toggleGlobalSaveAndExitDialog",
    ]),
    formatRepeatingGroupsCount,
    loadSaveAndExitDialog() {
      this.toggleGlobalSaveAndExitDialog();
    },
    saveAndExit(reference) {
      this.setOverlayVisible(true);
      this.$refs.saveAndExitDialog.cancel();
      if (this.isSelfServe) {
        this.handleD2CSaveAndExitOnResults().then(() => {
          this.setOverlayVisible(false);
        });
      } else {
        this.handleSaveAndExitOnResults(reference).then(() => {
          this.setOverlayVisible(false);
        });
      }
    },
    closeSaveAndExitDialog() {
      this.$refs.saveAndExitDialog.cancel();
    },
    backToPropertyDetails() {
      if (this.handleBackBtnExternally) {
        this.$emit("click:backButton");
        return;
      }

      if (this.replacementProductAvailable) {
        this.$emit("showReplacementProductDialog");
      } else {
        this.handleBack({ section: this.section });
      }
    },
    copyText(text) {
      navigator.clipboard.writeText(text);
    },
  },
};
</script>

<style lang="scss" scoped>
.referenceNumber {
  display: inline-flex;
  align-items: center;
  margin-top: 5px;
  padding: 2px 8px;
  border-radius: 4px;
  color: $black-2;
  font-weight: 700;
  background-color: transparentize($secondary, 0.5);
}

.referenceNumber__icon {
  display: inline-flex;
  align-items: center;
}

.referenceNumber__text {
  text-decoration: none;
  cursor: copy;
}

.referenceNumber:hover .referenceNumber__text {
  text-decoration: underline;
}

.referenceNumber__icon {
  text-decoration: none;
  cursor: copy;
}

@media screen and (max-width: ($grid-breakpoints-md - 1)) {
  .additional-content {
    border-top: 1px solid #ccd6dd;
  }
}

@media screen and (min-width: ($grid-breakpoints-lg)) {
  .content-cover__wrapper {
    max-width: 171px;
  }
}
</style>

<!-- Global styles for teleported tooltip content -->
<style lang="scss">
.qs-tooltip {
  padding: 4px 8px;
}

.qs-tooltip::after {
  content: "";
  position: absolute;
  top: -4px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 10px;
  height: 10px;
  background: inherit;
  pointer-events: none;
}

.menuable__content__active.qs-tooltip {
  opacity: 1 !important;
}
</style>
